/**
 * Parses a single bedroom token from property_bedrooms_range (e.g. "studio", "2", "std").
 */
function parseBedroomToken(part: string): number | 'studio' | null {
  const trimmed = part.trim().toLowerCase();
  if (trimmed === 'studio' || trimmed === 'std' || trimmed === '0') return 'studio';
  const num = parseInt(trimmed, 10);
  if (!isNaN(num) && num > 0) return num;
  return null;
}

function formatBedroomLabel(value: number | 'studio'): string {
  if (value === 'studio') return 'Studio';
  return String(value);
}

/**
 * Expands a bedrooms range into individual labels without redundant bedroom wording.
 * "1-3" → ["1", "2", "3"]; "studio - 2" → ["Studio", "1", "2"].
 */
function expandBedroomsRange(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];

  const parts = raw.trim().split(/\s*-\s*/);
  if (parts.length === 1) {
    const value = parseBedroomToken(parts[0]);
    return value ? [formatBedroomLabel(value)] : [];
  }

  const start = parseBedroomToken(parts[0]);
  const end = parseBedroomToken(parts[parts.length - 1]);

  if (!start || !end) {
    return parts
      .map((part) => {
        const value = parseBedroomToken(part);
        return value ? formatBedroomLabel(value) : part.trim();
      })
      .filter(Boolean);
  }

  if (start === 'studio' && end === 'studio') return ['Studio'];

  if (start === 'studio' && typeof end === 'number') {
    const labels: string[] = ['Studio'];
    for (let i = 1; i <= end; i++) labels.push(String(i));
    return labels;
  }

  if (typeof start === 'number' && typeof end === 'number') {
    if (start === end) return [String(start)];
    const labels: string[] = [];
    for (let i = start; i <= end; i++) labels.push(String(i));
    return labels;
  }

  const labels = [formatBedroomLabel(start), formatBedroomLabel(end)];
  return labels.filter((label, index) => labels.indexOf(label) === index);
}

/**
 * Formats bedroom counts as an Oxford-style list: "1, 2, & 3" or "Studio & 2".
 * Omits "bedroom", "bedrooms", "bed", and "beds" — callers provide page context.
 */
export function formatBedroomsList(raw: string | null | undefined): string {
  const labels = expandBedroomsRange(raw);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}
